import clsx from 'clsx'
import first from 'lodash/first'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import React, {
  Dispatch,
  FC,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import SvgAngleLeft from '@Assets/SVG/AngleLeft'
import SvgCheck from '@Assets/SVG/Check'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './SelectInput.module.scss'

export type SelectInputValidationResult = { errorMessage: string, success: boolean }
export type SelectInputValidationFn = (value: string) => SelectInputValidationResult
export type SelectInputActionProps = {
  touch: () => void
  validate: (value?: string) => boolean | undefined
}
interface TextInputProps {
  actionRef?: MutableRefObject<SelectInputActionProps | null>
  autoComplete?: string
  className?: string
  defaultTouched?: boolean
  deselect?: string
  disabled?: boolean
  disabledOptions?: string[]
  id?: string
  label?: string | null
  onChange: (value: string, valid: boolean) => void
  options?: string[]
  preventDefault?: boolean
  setValue?: Dispatch<SetStateAction<string>> | ((value: string) => void)
  validations?: Array<SelectInputValidationFn>
  value: string
}
export const SelectInput: FC<PropsWithChildren<TextInputProps>> = ({
  autoComplete,
  preventDefault,
  actionRef,
  className,
  deselect,
  disabled,
  label,
  value,
  disabledOptions = [],
  id,
  onChange,
  validations = [],
  setValue,
  defaultTouched = false,
  options,
}) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const ref = useRef<HTMLSelectElement | null>(null)
  const isOpen = useRef<boolean>(false)
  const [touched, setTouched] = useState(defaultTouched)
  const [errors, setErrors] = useState<Array<SelectInputValidationResult>>([])
  const hasError = useMemo(() => touched && validations.length > 0 && errors.length > 0, [errors, touched, validations.length])
  const isValid = useMemo(() => touched && validations.length > 0 && errors.length === 0, [errors, touched, validations.length])

  const runValidations: (newValue?: string) => boolean = useCallback(
    (newValue?: string) => {
      if (!isNil(newValue) && setValue) {
        setValue(newValue)
      }
      const validationErrors: Array<SelectInputValidationResult> = []
      forEach(validations, (validationFn) => {
        const result: SelectInputValidationResult = validationFn(newValue || value)
        if (!result?.success) {
          validationErrors.push(result)
        }
      })
      setErrors(validationErrors)
      isOpen.current = false
      return validationErrors.length === 0
    },
    [value, validations, setValue],
  )

  useImperativeHandle(actionRef, () => ({
    validate: runValidations,
    touch: () => setTouched(true),
  }))

  const handleChange = useCallback((newValue: string) => {
    setTouched(true)
    isOpen.current = false
    onChange(newValue, runValidations(newValue))
  }, [onChange, runValidations])

  useEffect(() => {
    if (!isEmpty(value)) {
      runValidations()
    }
    if (defaultTouched) {
      runValidations(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={clsx(
        styles.inputContainer,
        hasError && styles.error,
        isValid && styles.success,
        className,
        cofDesignV1 && styles.cofDesignV1,
      )}
    >
      <select
        aria-label={typeof label === 'string' ? label : undefined}
        aria-labelledby={`${id}-label`}
        autoComplete={autoComplete}
        className={clsx(styles.inputField, !isEmpty(value) && styles.inputFieldWithValue, !isNil(label) && styles.withLabel)}
        disabled={disabled}
        id={id}
        onChange={e => handleChange(e.target.value)}
        onClick={(e) => {
          if (preventDefault) {
            e.preventDefault()
            e.stopPropagation()
          }
          isOpen.current = true
        }}
        ref={ref}
        value={value ?? first(options)}
      >
        {!isNil(deselect) && <option value={undefined}>{!isOpen.current && value === '' ? ' ' : deselect}</option>}
        {map(options, option => (
          <option key={option} disabled={disabledOptions.includes(option)} value={option}>
            {t(option)}
          </option>
        ))}

      </select>
      <label
        className={clsx(
          styles.inputLabel,
          !!value && styles.labelUp,
        )}
        htmlFor={id}
        id={`${id}-label`}
      >
        {label}
      </label>
      <div className={clsx(styles.arrow, disabled && styles.disabled)}>
        {!isValid && (
          <SvgAngleLeft className={clsx(
            !!isOpen.current && styles.isOpen,
            !isOpen.current && styles.isClosed,
          )}
          />
        )}
      </div>
      {isValid && (
        <div className={styles.checkmark}>
          <SvgCheck />
        </div>
      )}
      {hasError && (
        <span className={clsx(styles.error, 'error')}>
          {cofDesignV1 && (
            <>
              {t('common:ERROR_LABEL')}
              {': '}
            </>
          )}
          {t(first(errors)!.errorMessage)}
        </span>
      )}
    </div>
  )
}
